import { ArrowDown, ArrowUp, CalendarDays, CalendarPlus, ListChecks, MoreVertical } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { Menu } from '@/components/ui/Menu';
import { Table, type Column } from '@/components/ui/Table';
import {
  buildCalendarFile,
  downloadCalendarFile,
} from '@/features/institute/calendar/utils/ics';
import { subjectTheme } from '@/features/institute/timetable/subjectTheme';
import { cn } from '@/lib/utils';
import type { PaginationMeta } from '@/types/api';
import type { ParentTaskItem } from '../../types/parent';
import {
  TASK_PRIORITY_LABEL,
  WORK_STATE_LABEL,
  formatDueDay,
  workState,
} from '../../utils';

/** Which way the due-date column is sorted. */
export type TaskSortDirection = 'asc' | 'desc';

export interface TaskTableProps {
  /** Only the rows on this page — the page above does the slicing. */
  rows: ParentTaskItem[];
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  today: string;
  sortDirection: TaskSortDirection;
  onToggleSort: () => void;
  /** Opens the whole task — its description, instructions and attachment. */
  onOpen: (uuid: string) => void;
  onClearFilters: () => void;
  isFiltered: boolean;
}

/**
 * The tasks, as a table.
 *
 * A table rather than the list the Homework tab uses, because tasks are read
 * across rather than down: a parent is comparing six rows on priority and date
 * to decide what happens this evening, and columns are what makes that one
 * glance instead of six.
 *
 * The whole row opens the task. The design gives each row a menu as well, and
 * the two are not redundant — the row is "let me read this", the menu is the
 * two things a parent then wants to *do* with it, which should not need the
 * task opened first.
 */
export function TaskTable({
  rows,
  meta,
  onPageChange,
  today,
  sortDirection,
  onToggleSort,
  onOpen,
  onClearFilters,
  isFiltered,
}: TaskTableProps) {
  /**
   * One deadline as a calendar file.
   *
   * The academic calendar's builder, not a second one written here: it already
   * knows that an all-day event's DTEND is exclusive and that Outlook rejects
   * unfolded lines (CLAUDE.md §3).
   */
  const addToCalendar = (task: ParentTaskItem) => {
    const file = buildCalendarFile(
      [
        {
          uuid: task.uuid,
          // A deadline is not a row in either calendar table; it is a task being
          // exported, and `source` says which screen may change it.
          source: 'EVENT',
          title: `${task.subject}: ${task.title}`,
          startDate: task.dueDate,
          endDate: task.dueDate,
          startTime: null,
          endTime: null,
          category: 'ACADEMIC',
          description: task.description,
          audience: 'PARENTS',
          classScope: 'ALL_CLASSES',
          days: 1,
        },
      ],
      task.title,
    );

    downloadCalendarFile(file, `${task.uuid}.ics`);
  };

  const columns: Column<ParentTaskItem>[] = [
    {
      key: 'task',
      header: 'Task',
      sticky: 'left',
      stickyWidth: 280,
      className: '!pl-4 !pr-4 min-w-[260px]',
      headerClassName: '!pl-4 !pr-4 min-w-[260px]',
      render: (task) => {
        const theme = subjectTheme(task.subject);

        return (
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className={cn(
                'grid h-10 w-10 shrink-0 place-items-center rounded-xl',
                theme.iconBg,
              )}
            >
              {theme.icon}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                {task.title}
              </p>
              <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                Teacher: {task.teacher}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'subject',
      header: 'Subject',
      hideOnMobile: true,
      render: (task) => (
        <span className="rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/60 px-2.5 py-1 text-xs font-bold">
          {task.subject}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (task) => (
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold',
            task.priority === 'HIGH' &&
              'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20',
            task.priority === 'MEDIUM' &&
              'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
            task.priority === 'LOW' &&
              'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
          )}
        >
          {TASK_PRIORITY_LABEL[task.priority]}
        </span>
      ),
    },
    {
      key: 'due',
      header: (
        <button
          type="button"
          onClick={onToggleSort}
          aria-label={`Sort by due date, ${sortDirection === 'asc' ? 'soonest' : 'latest'} first`}
          className="inline-flex items-center gap-1 transition-colors hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer"
        >
          <span>Due date</span>
          {sortDirection === 'asc' ? (
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      ),
      hideOnMobile: true,
      render: (task) => {
        const [date, weekday] = formatDueDay(task.dueDate).split(', ');

        return (
          <div className="flex items-start gap-1.5">
            <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
            <div>
              <p className="whitespace-nowrap text-sm font-bold text-slate-900 dark:text-slate-100">{date}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{weekday}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (task) => {
        const state = workState(task, today);

        return (
          <span
            className={cn(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold',
              state === 'COMPLETED' &&
                'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
              state === 'OVERDUE' &&
                'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20',
              (state === 'PENDING' || state === 'UPCOMING') &&
                'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
            )}
          >
            {WORK_STATE_LABEL[state]}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      sticky: 'right',
      stickyWidth: 80,
      className: '!px-4',
      headerClassName: '!px-4',
      render: (task) => (
        <span
          className="inline-flex"
          onClick={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Menu
            label={`Actions for ${task.title}`}
            triggerClassName="h-8 w-8 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white rounded-lg transition-colors"
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            items={[
              {
                key: 'open',
                label: 'View full details',
                description: 'Description, instructions and any worksheet',
                icon: <ListChecks className="h-4 w-4" aria-hidden />,
                onSelect: () => onOpen(task.uuid),
              },
              {
                key: 'calendar',
                label: 'Add the deadline to my calendar',
                description: 'Saves a calendar file for this due date',
                icon: <CalendarPlus className="h-4 w-4" aria-hidden />,
                onSelect: () => addToCalendar(task),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  const startRecord = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endRecord = Math.min(meta.page * meta.limit, meta.total);

  return (
    <section
      className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-xs flex flex-col justify-between"
      aria-label="Tasks list"
    >
      <div>
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-3.5 sm:px-5">
          <h2 className="text-sm sm:text-base font-bold tracking-tight text-slate-800 dark:text-white">
            Tasks list
          </h2>
          <span className="rounded-full bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
            {meta.total}
          </span>
        </header>

        <Table
          variant="plain"
          columns={columns}
          rows={rows}
          rowKey={(task) => task.uuid}
          onRowClick={(task) => onOpen(task.uuid)}
          empty={
            <EmptyState
              icon={<ListChecks className="h-5 w-5" />}
              title={isFiltered ? 'No tasks match these filters' : 'No tasks yet'}
              description={
                isFiltered
                  ? 'Nothing here answers to every filter at once. Widen one of them to see more.'
                  : 'Once your child’s teachers set tasks, every one of them appears here with its due date.'
              }
              action={
                isFiltered ? (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="text-sm font-bold text-indigo-600 dark:text-indigo-400 underline-offset-4 hover:underline cursor-pointer"
                  >
                    Clear the filters
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {/* Pagination Footer */}
      {meta.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-slate-100 dark:border-slate-800 text-xs font-medium text-slate-500 dark:text-slate-400">
          <div>
            Showing {startRecord} to {endRecord} of {meta.total} tasks
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              aria-label="Previous page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
            >
              <span className="sr-only">Previous</span>
              &lt;
            </button>

            {Array.from({ length: meta.totalPages }, (_, i) => i + 1).map((pageNum) => {
              const isActive = pageNum === meta.page;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => onPageChange(pageNum)}
                  className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => onPageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              aria-label="Next page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
            >
              <span className="sr-only">Next</span>
              &gt;
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
